import React, { useEffect, useRef, useState } from 'react';
import { Square, Circle, RotateCcw, Camera, RefreshCw, Scissors, Check } from 'lucide-react';
import { Button } from './ui/button';

/**
 * MediaRecorder wrapper with front/back camera toggle and optional trim step.
 *  onRecorded(file, previewUrl) fires whenever the current clip changes
 *  (initial stop OR after trim). Passing null clears.
 */
export default function VideoRecorder({ onRecorded, onClear }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const previewRef = useRef(null);          // <video> that plays the recorded blob
  const trimStopTimerRef = useRef(null);

  const [state, setState] = useState('idle'); // idle | ready | recording | recorded | trimming | error
  const [error, setError] = useState('');
  const [seconds, setSeconds] = useState(0);
  const [previewUrl, setPreviewUrl] = useState('');
  const [facing, setFacing] = useState('environment'); // 'environment' (back) | 'user' (front)

  // Trim state
  const [duration, setDuration] = useState(0);
  const [inSec, setInSec] = useState(0);
  const [outSec, setOutSec] = useState(0);
  const [trimming, setTrimming] = useState(false);

  const startCamera = async (facingModeOverride) => {
    setError('');
    // stop old tracks first
    try { streamRef.current?.getTracks?.().forEach(t => t.stop()); } catch (_) {}
    streamRef.current = null;
    const fm = facingModeOverride || facing;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: fm }, width: { ideal: 1280 } },
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        await videoRef.current.play().catch(() => {});
      }
      setState('ready');
    } catch (e) {
      setError(e?.message || 'Camera unavailable');
      setState('error');
    }
  };

  const switchCamera = async () => {
    const next = facing === 'environment' ? 'user' : 'environment';
    setFacing(next);
    if (state === 'ready' || state === 'error') {
      await startCamera(next);
    }
  };

  const stopCamera = () => {
    try { streamRef.current?.getTracks?.().forEach(t => t.stop()); } catch (_) {}
    streamRef.current = null;
  };

  useEffect(() => () => stopCamera(), []);

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    setSeconds(0);
    const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
      ? 'video/webm;codecs=vp9,opus'
      : (MediaRecorder.isTypeSupported('video/webm') ? 'video/webm' : '');
    const rec = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined);
    recorderRef.current = rec;
    rec.ondataavailable = (e) => e.data && e.data.size > 0 && chunksRef.current.push(e.data);
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mime || 'video/webm' });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      const file = new File([blob], `recording-${Date.now()}.webm`, { type: blob.type });
      setState('recorded');
      onRecorded?.(file, url);
      stopCamera();
    };
    rec.start(1000);
    setState('recording');
    const iv = setInterval(() => {
      setSeconds(s => {
        if (recorderRef.current?.state !== 'recording') { clearInterval(iv); return s; }
        return s + 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    try { recorderRef.current?.stop(); } catch (_) {}
  };

  const reset = () => {
    setPreviewUrl(u => { if (u) URL.revokeObjectURL(u); return ''; });
    setState('idle');
    setSeconds(0);
    setDuration(0);
    setInSec(0);
    setOutSec(0);
    onClear?.();
  };

  // Wire duration on preview load
  const onPreviewLoaded = () => {
    const d = previewRef.current?.duration;
    if (d && isFinite(d)) {
      const rounded = Math.max(1, Math.floor(d * 10) / 10);
      setDuration(rounded);
      setInSec(0);
      setOutSec(rounded);
    }
  };

  const enterTrim = () => setState('trimming');
  const cancelTrim = () => setState('recorded');

  const applyTrim = async () => {
    if (!previewRef.current) return;
    if (outSec - inSec < 0.3) {
      setError('Trim range too short.');
      return;
    }
    setTrimming(true);
    setError('');
    try {
      const video = previewRef.current;
      video.muted = false;
      video.currentTime = inSec;
      await new Promise((res) => {
        const onSeek = () => { video.removeEventListener('seeked', onSeek); res(); };
        video.addEventListener('seeked', onSeek);
      });

      const stream = typeof video.captureStream === 'function'
        ? video.captureStream()
        : (typeof video.mozCaptureStream === 'function' ? video.mozCaptureStream() : null);
      if (!stream) throw new Error('Trim not supported on this browser.');

      const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
        ? 'video/webm;codecs=vp9,opus'
        : (MediaRecorder.isTypeSupported('video/webm') ? 'video/webm' : '');
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      rec.ondataavailable = (e) => e.data && e.data.size > 0 && chunks.push(e.data);
      const stopped = new Promise((res) => { rec.onstop = () => res(); });
      rec.start(250);

      // Play video and stop recorder at outSec
      await video.play();
      const wallStart = performance.now();
      const targetDurationMs = (outSec - inSec) * 1000;
      const check = () => {
        const elapsed = performance.now() - wallStart;
        const at = video.currentTime;
        if (at >= outSec - 0.05 || elapsed >= targetDurationMs + 500 || video.ended) {
          try { rec.stop(); } catch (_) {}
          try { video.pause(); } catch (_) {}
          return;
        }
        trimStopTimerRef.current = requestAnimationFrame(check);
      };
      trimStopTimerRef.current = requestAnimationFrame(check);

      await stopped;
      cancelAnimationFrame(trimStopTimerRef.current);
      const blob = new Blob(chunks, { type: mime || 'video/webm' });
      const url = URL.createObjectURL(blob);
      const file = new File([blob], `recording-${Date.now()}-trim.webm`, { type: blob.type });

      // Swap preview to the trimmed blob
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url);
      onRecorded?.(file, url);
      setState('recorded');
      // Reset trim range to full new duration
      setInSec(0);
      setOutSec(0);
      setDuration(0);
    } catch (e) {
      setError(e?.message || 'Could not trim clip.');
      setState('recorded');
    } finally {
      setTrimming(false);
    }
  };

  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  return (
    <div className="space-y-3" data-testid="video-recorder">
      <div className="relative aspect-video bg-black rounded-lg overflow-hidden border border-slate-200">
        {state === 'recorded' || state === 'trimming' ? (
          <video ref={previewRef} src={previewUrl} controls={state === 'recorded'} playsInline
            onLoadedMetadata={onPreviewLoaded}
            className="w-full h-full" data-testid="video-recorder-preview" />
        ) : (
          <video ref={videoRef} playsInline muted className="w-full h-full object-cover" />
        )}
        {state === 'recording' && (
          <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-rose-500/95 text-white text-xs font-semibold px-2 py-1 rounded-full">
            <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
            REC  {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}
          </div>
        )}
        {(state === 'ready' || state === 'recording') && (
          <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] tracking-wider uppercase px-2 py-1 rounded-full">
            {facing === 'environment' ? 'Back' : 'Front'} cam
          </div>
        )}
        {(state === 'idle' || state === 'error') && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500 text-sm gap-3">
            <Camera className="h-8 w-8" />
            <div>{error || 'Camera preview will appear here'}</div>
          </div>
        )}
      </div>

      {/* Trim UI */}
      {state === 'trimming' && duration > 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid="video-recorder-trim-panel">
          <div className="flex items-center justify-between text-xs text-slate-600">
            <span>In {fmt(inSec)}</span>
            <span className="text-slate-400">Kept: {fmt(Math.max(0, outSec - inSec))}</span>
            <span>Out {fmt(outSec)}</span>
          </div>
          <div className="space-y-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Start</label>
              <input type="range" min={0} max={duration} step="0.1" value={inSec}
                data-testid="trim-in-slider"
                onChange={e => {
                  const v = Math.min(parseFloat(e.target.value), outSec - 0.3);
                  setInSec(v);
                  if (previewRef.current) previewRef.current.currentTime = v;
                }}
                className="w-full accent-sky-500" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">End</label>
              <input type="range" min={0} max={duration} step="0.1" value={outSec}
                data-testid="trim-out-slider"
                onChange={e => {
                  const v = Math.max(parseFloat(e.target.value), inSec + 0.3);
                  setOutSec(v);
                  if (previewRef.current) previewRef.current.currentTime = v;
                }}
                className="w-full accent-sky-500" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={applyTrim} disabled={trimming}
              data-testid="trim-apply"
              className="bg-sky-500 hover:bg-sky-400 text-slate-900 font-semibold">
              <Check className="h-4 w-4 mr-1.5" /> {trimming ? 'Trimming' : 'Apply trim'}
            </Button>
            <Button type="button" variant="outline" onClick={cancelTrim} disabled={trimming}
              data-testid="trim-cancel"
              className="border-slate-300 text-slate-700 bg-transparent hover:bg-slate-100">
              Cancel
            </Button>
          </div>
          <p className="text-[11px] text-slate-500">Drag the sliders to keep only the meaningful shift. The trimmed clip will be what gets uploaded.</p>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {state === 'idle' && (
          <>
            <Button type="button" onClick={() => startCamera()} data-testid="recorder-enable-camera"
              className="bg-sky-500 hover:bg-sky-400 text-slate-900 font-semibold">
              <Camera className="h-4 w-4 mr-1.5" /> Enable camera
            </Button>
            <Button type="button" variant="outline" onClick={switchCamera} data-testid="recorder-switch-camera"
              className="border-slate-300 text-slate-700 bg-transparent hover:bg-slate-100">
              <RefreshCw className="h-4 w-4 mr-1.5" /> {facing === 'environment' ? 'Front cam' : 'Back cam'}
            </Button>
          </>
        )}
        {state === 'ready' && (
          <>
            <Button type="button" onClick={startRecording} data-testid="recorder-start"
              className="bg-rose-500 hover:bg-rose-400 text-white font-semibold">
              <Circle className="h-4 w-4 mr-1.5" fill="currentColor" /> Start recording
            </Button>
            <Button type="button" variant="outline" onClick={switchCamera} data-testid="recorder-switch-camera"
              className="border-slate-300 text-slate-700 bg-transparent hover:bg-slate-100">
              <RefreshCw className="h-4 w-4 mr-1.5" /> {facing === 'environment' ? 'Front cam' : 'Back cam'}
            </Button>
          </>
        )}
        {state === 'recording' && (
          <Button type="button" onClick={stopRecording} data-testid="recorder-stop"
            className="bg-slate-100 hover:bg-slate-200 text-slate-900">
            <Square className="h-4 w-4 mr-1.5" fill="currentColor" /> Stop
          </Button>
        )}
        {state === 'recorded' && (
          <>
            <Button type="button" onClick={enterTrim} data-testid="recorder-trim"
              className="bg-sky-500 hover:bg-sky-400 text-slate-900 font-semibold">
              <Scissors className="h-4 w-4 mr-1.5" /> Trim clip
            </Button>
            <Button type="button" variant="outline" onClick={reset} data-testid="recorder-reset"
              className="border-slate-300 text-slate-700 bg-transparent hover:bg-slate-100">
              <RotateCcw className="h-4 w-4 mr-1.5" /> Record again
            </Button>
          </>
        )}
        {state === 'error' && (
          <Button type="button" onClick={() => startCamera()}
            className="bg-slate-100 hover:bg-slate-200 text-slate-900">
            <RotateCcw className="h-4 w-4 mr-1.5" /> Retry
          </Button>
        )}
      </div>
    </div>
  );
}
